import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box, TextField, Button, Typography, Alert,
  InputAdornment, IconButton, CircularProgress, Stack, Link, alpha, useTheme,
  Collapse, Divider, Chip,
} from '@mui/material';
import {
  Email, Lock, Phone, Visibility, VisibilityOff, ArrowBack, Public, Tune, AutoAwesome,
  ErrorOutline, VpnKey,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import { setCredentials } from '../../store/slices/authSlice';
import { setTenant } from '../../store/slices/tenantSlice';
import { getRoleHomePath } from '../../config/roleNavigation';
import { AuthLayout, FormStack, PhoneNumberField } from '../../components/ui/forms';
import { useI18n } from '../../i18n/I18nProvider';
import { redeemDemoKey } from '../../services/salesDemoService';
import { resolveSuperAdminTenantSlug } from '../../hooks/useTenantBootstrap';

interface LoginForm {
  email: string;
  password: string;
}

interface OtpForm {
  phone: string;
  otp: string;
}

const DEMO_ACCOUNTS = [
  { role: 'Super Admin', email: 'satechtechnology.com', password: 'admin@123', tenantSlug: '' },
  { role: 'Driver100 Admin', email: 'admin@driver100.com', password: 'Admin@123', tenantSlug: 'driver100' },
  { role: 'Telimani100 Admin', email: 'admin@telimani100.com', password: 'Admin@123', tenantSlug: 'telimani100' },
  { role: 'Client Admin (demo)', email: 'admin@demo.com', password: 'Admin@2024', tenantSlug: 'demo' },
  { role: 'City Admin (Zone)', email: 'cityadmin@demo.com', password: 'CityAdmin@2024', tenantSlug: 'demo' },
  { role: 'Employee', email: 'employee@demo.com', password: 'Employee@2024', tenantSlug: 'demo' },
  { role: 'Driver', email: 'driver@demo.com', password: 'Driver@2024', tenantSlug: 'demo' },
  { role: 'Delivery Partner', email: 'delivery@demo.com', password: 'Delivery@2024', tenantSlug: 'demo' },
  { role: 'Service Provider', email: 'provider@demo.com', password: 'Provider@2024', tenantSlug: 'demo' },
  { role: 'Customer', email: 'customer@demo.com', password: 'Customer@2024', tenantSlug: 'demo' },
];

const isDevLogin = import.meta.env.DEV;

export default function LoginPage() {
  const theme = useTheme();
  const { t } = useI18n();
  const [mode, setMode] = useState<'email' | 'otp' | 'demo'>('email');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [demoKey, setDemoKey] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '' } });
  const otpForm = useForm<OtpForm>({ defaultValues: { phone: '', otp: '' } });

  const applyLoginTenant = (slug: string) => {
    const next = slug.trim().toLowerCase();
    if (next) {
      localStorage.setItem('tenantSlug', next);
      dispatch(setTenant({ slug: next }));
    } else {
      localStorage.removeItem('tenantSlug');
      dispatch(setTenant({ slug: '', companyName: '' }));
    }
  };

  useEffect(() => {
    const tenant = (searchParams.get('tenant') || searchParams.get('slug') || '').trim().toLowerCase();
    if (tenant) {
      applyLoginTenant(tenant);
    }
    const reason = (searchParams.get('reason') || '').trim().toUpperCase();
    if (reason === 'TENANT_SUSPENDED') {
      setError('This tenant is suspended. Contact platform support.');
    } else if (reason === 'TENANT_DEACTIVATED') {
      setError('This tenant is deactivated — the package/plan period has ended or access was closed. Contact platform support to renew.');
    } else if (reason === 'DEMO_EXPIRED' || reason === 'DEMO_LOCKED') {
      setError('This demo is no longer available. Contact platform support.');
    } else if (reason === 'TENANT_INACTIVE') {
      setError('This tenant is inactive. Contact platform support.');
    }
  }, [searchParams]);

  const loadTenantContext = async (user: { userType: string; tenantId?: string }, enabledFeatures?: string[]) => {
    if (user.userType === 'super_admin') {
      // Always start on the first client tenant; switcher can change afterward.
      const picked = await resolveSuperAdminTenantSlug({ forceFirst: true, preferStored: false });
      if (picked) {
        localStorage.setItem('tenantSlug', picked.slug);
      } else {
        localStorage.removeItem('tenantSlug');
      }

      try {
        if (!picked) {
          dispatch(setTenant({
            slug: '',
            companyName: '',
            enabledFeatures: enabledFeatures || [],
            isDemo: false,
            demoExpiresAt: null,
          }));
          return;
        }
        const [tenantRes, modulesRes] = await Promise.all([api.get('/tenants/current'), api.get('/modules')]);
        dispatch(setTenant({
          id: tenantRes.data.data._id,
          companyName: tenantRes.data.data.companyName,
          slug: tenantRes.data.data.slug,
          branding: tenantRes.data.data.branding,
          modules: tenantRes.data.data.modules,
          currency: tenantRes.data.data.currency,
          enabledFeatures: enabledFeatures || modulesRes.data.data.enabledFeatures || [],
          isDemo: Boolean(tenantRes.data.data.isDemo),
          demoExpiresAt: tenantRes.data.data.demoExpiresAt || null,
        }));
      } catch {
        dispatch(setTenant({
          slug: picked?.slug || '',
          companyName: picked?.companyName || '',
          enabledFeatures: enabledFeatures || [],
          isDemo: false,
          demoExpiresAt: null,
        }));
      }
      return;
    }

    const slug = localStorage.getItem('tenantSlug') || '';
    if (slug) localStorage.setItem('tenantSlug', slug);

    if (user.tenantId) {
      try {
        const [tenantRes, modulesRes] = await Promise.all([api.get('/tenants/current'), api.get('/modules')]);
        dispatch(setTenant({
          id: tenantRes.data.data._id,
          companyName: tenantRes.data.data.companyName,
          slug: tenantRes.data.data.slug,
          branding: tenantRes.data.data.branding,
          modules: tenantRes.data.data.modules,
          currency: tenantRes.data.data.currency,
          enabledFeatures: enabledFeatures || modulesRes.data.data.enabledFeatures || [],
          isDemo: Boolean(tenantRes.data.data.isDemo),
          demoExpiresAt: tenantRes.data.data.demoExpiresAt || null,
        }));
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { code?: string; message?: string } }; status?: number };
        const code = ax.response?.data?.code;
        if (code === 'TENANT_SUSPENDED' || code === 'TENANT_DEACTIVATED' || code === 'TENANT_INACTIVE') {
          throw err;
        }
        dispatch(setTenant({
          slug: slug || '',
          companyName: '',
          enabledFeatures: enabledFeatures || [],
          isDemo: false,
          demoExpiresAt: null,
        }));
      }
    }
  };

  const handleDemoRedeem = async () => {
    setLoading(true);
    setError('');
    try {
      const key = demoKey.trim().toUpperCase().replace(/\s+/g, '');
      if (!key) {
        setError('Enter the demo key from your invite email');
        return;
      }
      const data = await redeemDemoKey(key, 'client_admin');
      localStorage.setItem('tenantSlug', data.tenant.slug);
      dispatch(setCredentials({
        user: data.user as never,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        permissions: data.permissions || [],
      }));
      dispatch(setTenant({
        id: data.tenant._id,
        companyName: data.tenant.companyName,
        slug: data.tenant.slug,
        branding: {
          primaryColor: data.tenant.branding?.primaryColor || '#173A5E',
          secondaryColor: data.tenant.branding?.secondaryColor || '#5C6B7A',
          logo: data.tenant.branding?.logo,
          favicon: data.tenant.branding?.favicon,
        },
        modules: data.tenant.modules || [],
        currency: data.tenant.currency || '',
        enabledFeatures: data.enabledFeatures || [],
        isDemo: true,
        demoExpiresAt: data.tenant.demoExpiresAt || null,
      }));
      navigate(getRoleHomePath(String(data.user.userType || 'client_admin')));
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Invalid or expired demo key');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (data: LoginForm) => {
    setLoading(true);
    setError('');
    try {
      const email = data.email.trim();
      const password = data.password;
      if (!email || !password) {
        setError('Email and password are required');
        return;
      }
      const known = DEMO_ACCOUNTS.find((a) => a.email.toLowerCase() === email.toLowerCase());
      if (known) {
        applyLoginTenant(known.tenantSlug);
      }
      const res = await api.post('/auth/login', { email, password });
      const tenantInfo = res.data.data.tenant as {
        slug?: string;
        companyName?: string;
        branding?: { primaryColor?: string; secondaryColor?: string; logo?: string; favicon?: string };
        modules?: { key: string; enabled: boolean }[];
        currency?: string;
        isDemo?: boolean;
        demoExpiresAt?: string;
        _id?: string;
      } | undefined;
      if (tenantInfo?.slug) {
        localStorage.setItem('tenantSlug', tenantInfo.slug);
      }
      dispatch(setCredentials(res.data.data));
      if (tenantInfo?.slug) {
        dispatch(setTenant({
          id: tenantInfo._id,
          companyName: tenantInfo.companyName,
          slug: tenantInfo.slug,
          branding: tenantInfo.branding,
          modules: tenantInfo.modules || [],
          currency: tenantInfo.currency || '',
          enabledFeatures: res.data.data.enabledFeatures || [],
          isDemo: Boolean(tenantInfo.isDemo),
          demoExpiresAt: tenantInfo.demoExpiresAt || null,
        }));
      } else {
        await loadTenantContext(res.data.data.user, res.data.data.enabledFeatures);
      }
      navigate(getRoleHomePath(res.data.data.user?.userType));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string }; status?: number }; message?: string; code?: string };
      if (!ax.response) {
        setError(isDevLogin
          ? (ax.code === 'ERR_NETWORK' ? 'Cannot reach API server. Is the backend running on port 5000?' : (ax.message || 'Login failed'))
          : 'Unable to sign in. Please try again.');
      } else if (ax.response.status === 401) {
        setError(ax.response?.data?.message || (isDevLogin
          ? 'Invalid email or password. Super Admin: satechtechnology.com / admin@123'
          : 'Invalid email or password.'));
      } else if (ax.response.status === 403) {
        setError(ax.response?.data?.message || 'Access denied for this tenant.');
      } else if (ax.response.status === 404) {
        setError(isDevLogin
          ? 'Tenant not found. Restart the backend (it auto-creates the demo tenant) or clear site data and retry.'
          : 'Account not found. Check the login link from your invite email.');
      } else {
        setError(ax.response?.data?.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (data: OtpForm) => {
    setLoading(true);
    setError('');
    localStorage.setItem('tenantSlug', 'demo');
    try {
      await api.post('/auth/otp/send', { phone: data.phone, purpose: 'login' });
      setOtpSent(true);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (data: OtpForm) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/otp/verify', { phone: data.phone, otp: data.otp, purpose: 'login' });
      dispatch(setCredentials(res.data.data));
      await loadTenantContext(res.data.data.user, res.data.data.enabledFeatures);
      navigate(getRoleHomePath(res.data.data.user?.userType));
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const featureItem = (icon: React.ReactNode, text: string) => (
    <Stack key={text} direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ mt: '2px', color: alpha('#fff', 0.72), display: 'flex' }}>{icon}</Box>
      <Typography variant="body2" sx={{ color: alpha('#fff', 0.82), fontWeight: 500, lineHeight: 1.5 }}>
        {text}
      </Typography>
    </Stack>
  );

  const sideContent = (
    <>
      <Typography
        variant="overline"
        sx={{ display: 'block', mb: 1.5, color: alpha('#fff', 0.5), letterSpacing: '0.16em', fontWeight: 700 }}
      >
        Operations platform
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: { md: '2.15rem', lg: '2.35rem' },
          letterSpacing: '-0.04em',
          lineHeight: 1.12,
          mb: 2,
        }}
      >
        One desk for every service you run
      </Typography>
      <Typography variant="body1" sx={{ color: alpha('#fff', 0.68), lineHeight: 1.7, mb: 4, maxWidth: 380 }}>
        Transport, food, grocery, porter, and on-demand — configured from metadata, not code.
      </Typography>
      <Stack spacing={2}>
        {featureItem(<Public fontSize="small" />, 'Multi-country, currency, and tenant')}
        {featureItem(<Tune fontSize="small" />, 'Zones, masters, workflows, live dispatch')}
        {featureItem(<AutoAwesome fontSize="small" />, 'White-label ready for enterprise clients')}
      </Stack>
    </>
  );

  return (
    <AuthLayout
      sideContent={sideContent}
      footer={isDevLogin ? 'Secured admin access' : 'Secured admin access'}
    >
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            fontSize: '1.45rem',
            letterSpacing: '-0.03em',
            mb: 0.6,
          }}
        >
          {mode === 'demo'
            ? 'Sales demo access'
            : mode === 'email'
              ? t('admin.page.dashboard_welcome', 'Welcome back')
              : t('admin.page.login_with_otp', 'OTP sign in')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3} sx={{ lineHeight: 1.6 }}>
          {mode === 'demo'
            ? 'Use the demo key from your invite email'
            : mode === 'email'
              ? t('admin.page.login_subtitle', 'Enter your credentials to continue')
              : t('admin.page.login_with_otp', 'We will send a code to your mobile')}
        </Typography>

        {error && (
          <Alert
            severity="error"
            variant="outlined"
            icon={<ErrorOutline fontSize="small" />}
            sx={{
              mb: 2.5,
              borderRadius: 2.5,
              alignItems: 'flex-start',
              bgcolor: alpha(theme.palette.error.main, 0.04),
              borderColor: alpha(theme.palette.error.main, 0.25),
            }}
          >
            {error}
          </Alert>
        )}

        {mode === 'demo' ? (
          <FormStack>
            <TextField
              label="Demo key"
              placeholder="XXXXX"
              fullWidth
              value={demoKey}
              onChange={(e) => setDemoKey(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKey fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              onClick={() => void handleDemoRedeem()}
              sx={{ py: 1.25, fontWeight: 600, fontSize: '0.9375rem', mt: 0.25 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Enter demo'}
            </Button>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              <Link
                component="button"
                type="button"
                underline="hover"
                fontWeight={700}
                onClick={() => { setMode('email'); setError(''); }}
                sx={{ cursor: 'pointer', border: 0, bgcolor: 'transparent', font: 'inherit' }}
              >
                Back to email sign in
              </Link>
            </Typography>
          </FormStack>
        ) : mode === 'email' ? (
          <form onSubmit={loginForm.handleSubmit(handleLogin)}>
            <FormStack>
              <TextField
                label={t('admin.page.login_email', 'Email / Username')}
                placeholder="satechtechnology.com"
                autoComplete="username"
                autoFocus
                fullWidth
                {...loginForm.register('email', { required: 'Email or username is required' })}
                error={!!loginForm.formState.errors.email}
                helperText={loginForm.formState.errors.email?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={t('admin.page.login_password', 'Password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                fullWidth
                {...loginForm.register('password', { required: 'Password is required' })}
                error={!!loginForm.formState.errors.password}
                helperText={loginForm.formState.errors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        tabIndex={-1}
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        aria-label="Toggle password"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                variant="contained"
                type="submit"
                size="large"
                disabled={loading}
                sx={{ py: 1.25, fontWeight: 600, fontSize: '0.9375rem', mt: 0.25 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : t('admin.page.login_submit', 'Sign in')}
              </Button>

              <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  color="text.secondary"
                  fontWeight={600}
                  fontSize="0.8125rem"
                  onClick={() => { setMode('otp'); setError(''); setOtpSent(false); otpForm.reset(); }}
                  sx={{ cursor: 'pointer', border: 0, bgcolor: 'transparent', font: 'inherit' }}
                >
                  {t('admin.page.login_with_otp', 'Sign in with OTP')}
                </Link>
              </Stack>
            </FormStack>
          </form>
        ) : (
          <form onSubmit={otpForm.handleSubmit(otpSent ? handleVerifyOtp : handleSendOtp)}>
            <FormStack>
              <Button
                startIcon={<ArrowBack fontSize="small" />}
                onClick={() => { setMode('email'); setError(''); setOtpSent(false); }}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'text.primary',
                  px: 1.25,
                  py: 0.75,
                  mb: 0.25,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                }}
              >
                Back to email login
              </Button>

              <PhoneNumberField
                label="Mobile number"
                placeholder="+919876543210"
                inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
                {...otpForm.register('phone', { required: true })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Collapse in={otpSent}>
                <TextField
                  label="OTP code"
                  placeholder="Enter 4-digit code"
                  {...otpForm.register('otp', { required: otpSent })}
                />
              </Collapse>

              <Button
                fullWidth
                variant="contained"
                type="submit"
                size="large"
                disabled={loading}
                sx={{ py: 1.25, fontWeight: 600, fontSize: '0.9375rem' }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : otpSent ? 'Verify & sign in' : 'Send OTP'}
              </Button>

              {isDevLogin && (
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Demo:{' '}
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  fontWeight={700}
                  onClick={() => {
                    otpForm.setValue('phone', '+919876543210');
                    otpForm.setValue('otp', '1234');
                    setOtpSent(true);
                  }}
                >
                  Customer
                </Link>
                {' · '}
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  fontWeight={700}
                  onClick={() => {
                    otpForm.setValue('phone', '+919876543211');
                    otpForm.setValue('otp', '1234');
                    setOtpSent(true);
                  }}
                >
                  Driver
                </Link>
              </Typography>
              )}
            </FormStack>
          </form>
        )}
      </Box>

      {isDevLogin && mode === 'email' && (
        <>
          <Divider />
          <Box sx={{ px: { xs: 3, sm: 4 }, py: 2.25 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              display="block"
              mb={1.25}
              sx={{ letterSpacing: '0.04em' }}
            >
              Quick demo
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {DEMO_ACCOUNTS.map((acc) => (
                <Chip
                  key={acc.email}
                  label={acc.role}
                  size="small"
                  variant="outlined"
                  clickable
                  tabIndex={-1}
                  onClick={() => {
                    setMode('email');
                    setError('');
                    loginForm.setValue('email', acc.email);
                    loginForm.setValue('password', acc.password);
                    applyLoginTenant(acc.tenantSlug);
                  }}
                  sx={{
                    fontWeight: 600,
                    borderRadius: '8px',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      borderColor: alpha(theme.palette.primary.main, 0.28),
                      color: 'text.primary',
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </>
      )}
    </AuthLayout>
  );
}
